import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLists, LIST_COLORS } from '../components/Lists';

function fmtMoney(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

const TYPE_LABEL = { deal: 'Pipeline Deal', mls: 'MLS Listing', off_market: 'Off-Market Lead' };
const TYPE_COLOR = { deal: '#6B6760', mls: '#14504A', off_market: '#14504A' };

export default function Lists() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lists, createList, renameList, deleteList, removeItem, reload } = useLists();

  const [members, setMembers] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');

  const selectedId = id ? Number(id) : null;
  const selected = lists.find(l => l.id === selectedId) || null;

  const loadMembers = useCallback((listId) => {
    if (!listId) { setMembers(null); return; }
    setLoadingMembers(true);
    fetch(`/api/lists/${listId}/items`)
      .then(r => r.json())
      .then(d => { setMembers(d.items || []); setLoadingMembers(false); })
      .catch(() => setLoadingMembers(false));
  }, []);

  useEffect(() => { loadMembers(selectedId); }, [selectedId, loadMembers, lists]);

  async function doCreate() {
    const name = newName.trim();
    if (!name) return;
    const list = await createList(name, LIST_COLORS[lists.length % LIST_COLORS.length]);
    setNewName('');
    setCreating(false);
    if (list?.id) navigate(`/lists/${list.id}`);
  }

  async function doRename() {
    const name = editName.trim();
    if (!name || !selected) return;
    await renameList(selected.id, { name });
    setEditing(false);
  }

  async function doDelete() {
    if (!selected) return;
    if (!window.confirm(`Delete the list "${selected.name}"? This won't delete the deals themselves.`)) return;
    await deleteList(selected.id);
    navigate('/lists');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', margin: '-32px -36px' }}>
      <div style={{ padding: '20px 28px 16px', borderBottom: '1px solid var(--navy-border)', flexShrink: 0 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 2 }}>Custom Lists</h1>
        <p style={{ fontSize: 12, color: 'var(--gray-500)' }}>Group deals, MLS listings, and off-market leads however you work — e.g. "Hot Deals" for agents to call first.</p>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {/* List of lists */}
        <div style={{ width: 280, overflowY: 'auto', borderRight: '1px solid var(--navy-border)', background: 'var(--navy)', padding: 12 }}>
          {lists.length === 0 && !creating && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>No lists yet.</div>
          )}
          {lists.map(l => (
            <div key={l.id} onClick={() => navigate(`/lists/${l.id}`)} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, marginBottom: 4, cursor: 'pointer',
              background: l.id === selectedId ? 'var(--navy-card)' : 'transparent',
              border: `1px solid ${l.id === selectedId ? 'var(--navy-border)' : 'transparent'}`,
            }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: l.color || 'var(--teal)', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 14, color: 'var(--white)', fontWeight: l.id === selectedId ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</span>
              <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{l.count}</span>
            </div>
          ))}
          {creating ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && doCreate()}
                placeholder="List name" style={{ flex: 1, fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--white)', outline: 'none' }} />
              <button onClick={doCreate} style={{ fontSize: 13, padding: '8px 12px', borderRadius: 8, background: 'var(--teal)', border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Add</button>
            </div>
          ) : (
            <button onClick={() => setCreating(true)} style={{ width: '100%', marginTop: 8, padding: '9px 12px', borderRadius: 8, background: 'transparent', border: '1px dashed var(--navy-border)', color: 'var(--teal)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              New List
            </button>
          )}
        </div>

        {/* Members */}
        <div style={{ flex: 1, overflowY: 'auto', background: 'var(--navy)', padding: 24 }}>
          {!selected ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--gray-500)', fontSize: 14, gap: 10 }}>
              Select a list to view its members
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <span style={{ width: 16, height: 16, borderRadius: '50%', background: selected.color, flexShrink: 0 }} />
                {editing ? (
                  <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                    <input autoFocus value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === 'Enter' && doRename()}
                      style={{ fontSize: 18, fontWeight: 700, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--white)', outline: 'none' }} />
                    <button onClick={doRename} style={btnSolid('var(--teal)')}>Save</button>
                    <button onClick={() => setEditing(false)} style={btnGhost()}>Cancel</button>
                  </div>
                ) : (
                  <>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--white)', margin: 0 }}>{selected.name}</h2>
                    <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>{selected.count} item{selected.count === 1 ? '' : 's'}</span>
                    <div style={{ flex: 1 }} />
                    <button onClick={() => { setEditName(selected.name); setEditing(true); }} style={btnGhost()}>Rename</button>
                    <button onClick={doDelete} style={btnGhost('#B85450')}>Delete</button>
                  </>
                )}
              </div>

              {loadingMembers ? (
                <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Loading…</div>
              ) : !members || members.length === 0 ? (
                <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '20px 0' }}>
                  This list is empty. Check deals on the Deal Pipeline, MLS Opportunities, or Off Market pages and click "Add to list".
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {members.map(m => (
                    <MemberRow key={`${m.item_type}:${m.item_id}`} member={m} navigate={navigate}
                      onRemove={() => removeItem(selected.id, m.item_type, m.item_id).then(() => loadMembers(selected.id))} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MemberRow({ member: m, navigate, onRemove }) {
  const d = m.detail;
  const tc = TYPE_COLOR[m.item_type] || 'var(--gray-500)';
  if (!d) {
    return (
      <div style={{ padding: '12px 14px', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, fontSize: 12, color: 'var(--gray-500)' }}>
        {TYPE_LABEL[m.item_type]} #{m.item_id} (record no longer available)
        <button onClick={onRemove} style={{ ...btnGhost('#B85450'), float: 'right' }}>Remove</button>
      </div>
    );
  }
  return (
    <div onClick={() => d._route && navigate(d._route)} style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', cursor: d._route ? 'pointer' : 'default',
      background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10,
    }}>
      <span style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 8, background: `${tc}20`, color: tc, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
        {TYPE_LABEL[m.item_type]}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.address || '—'}</div>
        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
          {[d.city, d.state, d.zip].filter(Boolean).join(', ')}
          {(d.beds || d.sqft) && <span> · {[d.beds && `${d.beds}bd`, d.baths && `${d.baths}ba`, d.sqft && `${d.sqft.toLocaleString()} sqft`].filter(Boolean).join(' · ')}</span>}
          {d.stage && <span> · {d.stage}</span>}
          {d.status && !d.stage && <span> · {d.status}</span>}
          {d.agent_name && <span> · Agent: {d.agent_name}{d.agent_phone ? ` (${d.agent_phone})` : ''}</span>}
        </div>
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {(d.ai_arv != null || d.list_price != null) && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>{fmtMoney(d.ai_arv ?? d.list_price)}</div>}
        {d.max_offer != null && <div style={{ fontSize: 11, color: 'var(--teal)' }}>MAO {fmtMoney(d.max_offer)}</div>}
      </div>
      <button onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Remove from list" style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 4 }}>×</button>
    </div>
  );
}

function btnSolid(color) { return { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, background: color, border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }; }
function btnGhost(color = 'var(--gray-500)') { return { padding: '7px 12px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color, fontSize: 12, fontWeight: 600, cursor: 'pointer' }; }
